import { Currency, CurrencySettings, HorizontalAlignment, ModelRow } from "@mcleod/core";
import { Image, Panel, PrintableEvent, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, Textbox } from "@mcleod/components";
import { CollectionsTableLayout } from "./Collections";
import { AutogenLayoutCollectionsGroupByExpand } from "./autogen/AutogenLayoutCollectionsGroupByExpand";
import { AutogenLayoutCollectionsGroupByTable } from "./autogen/AutogenLayoutCollectionsGroupByTable";
import { CollectionsUtil } from "./CollectionsUtil";

interface GroupByRowData {
    customerId: string;
    customerName: string;
    customerCurrency: string;
    invoiceCurrencyType: string;
    customerActionCode: string;
    customerActionCodeDescr: string;
    customerAveragePayDays: number;
    customerBillDueDays: number;
    defaultNextActionCode: string; // Default next action values are used on new call records.
    defaultNextActionDays: number; // They come from the customer's next action code's core file.
    defaultNextActionDescr: string;
    ageDays: number;
    balance: Currency;
    pastDueTotal: Currency;
    isBridgeAccount: boolean;
    bridgeId: string;
}

export class CollectionsGroupByTable extends AutogenLayoutCollectionsGroupByTable {
    private layoutCollectionsTable: CollectionsTableLayout;
    private groupByMap: Map<string, {
        rowData: GroupByRowData,
        expandedRowData: GroupByRowData[]
    }> = new Map();
    // Only one row can be selected at a time, but that row can be in a nested layout's table from an expanded row.
    // This means we need to keep track of the selected row at the top level.
    private selectedRow: TableRow = null;

    async initialize(layoutCollectionsTable: CollectionsTableLayout) {
        this.layoutCollectionsTable = layoutCollectionsTable;
        this.selectedRow = null; // Refresh button on Collections layout will call initialize again, so reset selectedRow
        this.setCustomFilter();
        await this.updateGroupByRows();
    }

    async updateGroupByRows() {
        if (this.tableGroupBy.selectedRows.length > 0) {
            this.tableGroupBy.selectedRows = [];
        }

        this.groupByMap.clear();

        // Build the groupByMap
        for (const row of this.sourceCollections.data) {
            const customerId = row.getString("customer_id");
            const customerName = row.getString("customer_name");
            const customerCurrency = row.getString("customer_currency", CurrencySettings.getTransactionalCurrency());
            const invoiceCurrencyType = row.getString("invoice_currency_type", customerCurrency);
            const ageDays = row.getNumber("age_days", 0);
            const balance = row.getCurrency("balance");
            const daysPastDue = row.getNumber("days_past_due", 0);

            if (!this.groupByMap.has(customerId)) {
                this.groupByMap.set(customerId, {
                    rowData: {
                        customerId: customerId,
                        customerName: customerName,
                        customerCurrency: customerCurrency,
                        invoiceCurrencyType: invoiceCurrencyType,
                        customerActionCode: row.getString("customer_action_code", null),
                        customerActionCodeDescr: row.getString("customer_action_code_descr", null),
                        customerAveragePayDays: row.getNumber("customer_average_pay_days", null),
                        customerBillDueDays: row.getNumber("customer_bill_due_days", null),
                        defaultNextActionCode: row.getString("default_next_action_code", null),
                        defaultNextActionDays: row.getNumber("default_next_action_days", -1),
                        defaultNextActionDescr: row.getString("default_next_action_descr", null),
                        ageDays: ageDays,
                        balance: new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() }),
                        pastDueTotal: new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() }),
                        isBridgeAccount: row.getBoolean("is_bridge_account", false),
                        bridgeId: row.getString("bridge_id", null)
                    },
                    expandedRowData: []
                });
            }

            const groupByRow = this.groupByMap.get(customerId);
            if (groupByRow != null) {
                if (ageDays > groupByRow.rowData.ageDays) {
                    groupByRow.rowData.ageDays = ageDays;
                }
                await Promise.all([
                    groupByRow.rowData.balance.add(balance),
                    daysPastDue > 0 && groupByRow.rowData.pastDueTotal.add(balance)
                ]);
            }
        }

        // Populate expanded row data, if necessary
        if (this.switchGroupBy.checked) {
            this.groupByMap.forEach((value) => {
                // If displaying customers by the bridge account, child account rows from the initial query will be stored separately.
                // They will be added to an expanded row's table whenever it is expanded.
                if (value.rowData.bridgeId) {
                    const bridgeAccountRow = this.groupByMap.get(value.rowData.bridgeId);
                    if (bridgeAccountRow && bridgeAccountRow.expandedRowData) {
                        // If a row is able to be expanded, add its own data to its expanded section
                        if (!this.expandedRowDataIncludesSelf(bridgeAccountRow.rowData.customerId, bridgeAccountRow.expandedRowData)) {
                            bridgeAccountRow.expandedRowData.push(bridgeAccountRow.rowData);
                        }
                        bridgeAccountRow.expandedRowData.push(value.rowData);
                    }
                }
            });
        }

        // Populate modelRows to be displayed
        const rows: ModelRow[] = [];
        for (const [key, value] of this.groupByMap.entries()) {
            if (this.shouldIncludeCustomer(key)) {
                const row = new ModelRow("lme/ar/collections", false);
                row.set("customer_id", key);
                row.set("customer_name", value.rowData.customerName);
                row.set("customer_currency", value.rowData.customerCurrency);
                row.set("invoice_currency_type", value.rowData.invoiceCurrencyType);
                row.set("customer_action_code", value.rowData.customerActionCode);
                row.set("customer_action_code_descr", value.rowData.customerActionCodeDescr);
                row.set("customer_average_pay_days", value.rowData.customerAveragePayDays);
                row.set("customer_bill_due_days", value.rowData.customerBillDueDays);
                row.set("default_next_action_code", value.rowData.defaultNextActionCode);
                row.set("default_next_action_days", value.rowData.defaultNextActionDays);
                row.set("default_next_action_descr", value.rowData.defaultNextActionDescr);
                row.set("age_days", value.rowData.ageDays);
                row.set("is_bridge_account", value.rowData.isBridgeAccount);
                row.set("bridge_id", value.rowData.bridgeId);

                if (this.switchGroupBy.checked && value.rowData.isBridgeAccount) {
                    const [balance, pastDueTotal] = await Promise.all([
                        this.calculateBalanceWithChildren(value.expandedRowData, value.rowData.invoiceCurrencyType),
                        this.calculatePastDueTotalWithChildren(value.expandedRowData, value.rowData.invoiceCurrencyType)
                    ]);
                    row.updateCurrency("balance", balance);
                    row.updateCurrency("past_due_total", pastDueTotal);
                } else {
                    row.updateCurrency("balance", value.rowData.balance);
                    row.updateCurrency("past_due_total", value.rowData.pastDueTotal);
                }

                rows.push(row);
            }
        }
        this.tableGroupBy.displayComponentData(null, rows, 0);
        this.tableGroupBy.applySort("customer_name");
    }

    async calculateBalanceWithChildren(customerExpandedRowData: GroupByRowData[], invoiceCurrencyType: string): Promise<Currency> {
        const totalBalance = new Currency({ amount: 0, currency_code: invoiceCurrencyType, conversion_date: new Date() });
        for (const child of customerExpandedRowData) {
            await totalBalance.add(child.balance);
        }
        return totalBalance;
    }

    async calculatePastDueTotalWithChildren(customerExpandedRowData: GroupByRowData[], invoiceCurrencyType: string): Promise<Currency> {
        const totalPastDue = new Currency({ amount: 0, currency_code: invoiceCurrencyType, conversion_date: new Date() });
        for (const child of customerExpandedRowData) {
            await totalPastDue.add(child.pastDueTotal);
        }
        return totalPastDue;
    }

    expandedRowDataIncludesSelf(customerId: string, expandedRowData: GroupByRowData[]): boolean {
        return expandedRowData.some(data => data.customerId === customerId);
    }

    shouldIncludeCustomer(customerId: string): boolean {
        // If Display by Bridge Account is checked, hide bridge accounts unless explicitly included in the profile.
        return !this.switchGroupBy.checked || this.isCustomerIncludedInProfile(customerId);
    }

    isCustomerIncludedInProfile(customerId: string): boolean {
        const profileRow = this.layoutCollectionsTable.getProfileRow();
        if (profileRow == null) {
            return false;
        }

        const profileCustomerIds = profileRow.getString("customer_ids", null);
        if (!profileCustomerIds) {
            return true;
        }

        const profileCustomerIdsList = profileCustomerIds.split(",").map(id => id.trim());

        if (profileRow.getBoolean("customer_exclude", false)) {
            return !profileCustomerIdsList.includes(customerId);
        } else {
            return profileCustomerIdsList.includes(customerId);
        }
    }

    async switchGroupByOnChange() {
        await this.updateGroupByRows();
        this.adjustBalanceColumnHeader();
        this.layoutCollectionsTable.selectedCustomerRow = null;
        this.refreshCustomFilter(this.switchGroupBy.checked);
        await this.layoutCollectionsTable.updateTableFooter();
    }

    textboxCustomerBalanceOnPrintable(event: PrintableEvent) {
        this.headerTextboxOnPrintable(event);
    }

    textboxCustomerPastDueOnPrintable(event: PrintableEvent) {
        this.headerTextboxOnPrintable(event);
    }

    headerTextboxOnPrintable(event: PrintableEvent) {
        const textbox = event.target as Textbox;
        textbox.captionProps = { align: HorizontalAlignment.RIGHT };
    }

    tableGroupByOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        tableRow.forEveryChildComponent((component) => {
            CollectionsUtil.setTooltipContent(component, modelRow);
        });

        const imageHasBridgeAccount = tableRow.findComponentById("imageHasBridgeAccount") as Image;
        if (imageHasBridgeAccount) {
            imageHasBridgeAccount.preventCollapse = !this.switchGroupBy.checked;
            imageHasBridgeAccount.visible = !this.switchGroupBy.checked &&
                modelRow.getString("bridge_id", null) != null;
        }

        tableRow.expandable = this.switchGroupBy.checked &&
            modelRow.getBoolean("is_bridge_account", false) &&
            this.groupByMap.get(modelRow.getString("customer_id"))?.expandedRowData?.length > 1; // More than just itself
    }

    async tableGroupByOnSelect() {
        if (this.selectedRow) {
            if (this.selectedRow.table !== this.tableGroupBy) {
                this.selectedRow.table.selectedRows = [];
            }
        }

        this.selectedRow = this.tableGroupBy.selectedRow ?? null;
        this.layoutCollectionsTable.selectedCustomerRow = (this.selectedRow?.data ?? null) as ModelRow;
        this.refreshCustomFilter(this.switchGroupBy.checked);
        await this.layoutCollectionsTable.updateTableFooter();
    }

    // START - Custom filter logic
    setCustomFilter() {
        this.layoutCollectionsTable.filterPanel.applyRemoveFilter = () => {
            this.layoutCollectionsTable.tableCollections.customFilter = this.getGroupByCustomFilter(this.switchGroupBy.checked);
        };

        // FilterPanel.updateFilterCounts will not consider the custom filter on the table, so we need to override it
        this.layoutCollectionsTable.filterPanel.updateFilterCounts = () => {
            const filters = this.layoutCollectionsTable.filterPanel.filters;
            const tableCollections = this.layoutCollectionsTable.tableCollections;

            // Reset filter counts
            for (const filter of filters) {
                filter.count = 0;
            }

            for (const tableRow of tableCollections.allRows) {
                for (const filter of filters) {
                    // Check both the task filter's condition and the groupBy table's current selection
                    if (filter.filter(tableRow.data) && this.shouldDisplayRow(tableRow, this.switchGroupBy.checked)) {
                        filter.count++;
                    }
                }
            }

            // Update filter button captions
            for (const filter of filters) {
                filter.button.caption = `${filter.caption} ${filter.count}`;
            }
        }

        this.layoutCollectionsTable.tableCollections.customFilter = this.getGroupByCustomFilter(this.switchGroupBy.checked);
        this.layoutCollectionsTable.filterPanel.updateFilterCounts();
    }

    refreshCustomFilter(includeChildAccounts: boolean) {
        this.layoutCollectionsTable.tableCollections.customFilter = this.getGroupByCustomFilter(includeChildAccounts);
        this.layoutCollectionsTable.filterPanel.updateFilterCounts();
    }

    getGroupByCustomFilter(includeChildAccounts: boolean): (row: TableRow) => boolean {
        return (row: TableRow) => {
            const activeTaskFilter = this.layoutCollectionsTable.filterPanel.getActiveFilter();
            const taskFilterMatches = activeTaskFilter == null || activeTaskFilter.filter(row.data);
            return taskFilterMatches && this.shouldDisplayRow(row, includeChildAccounts);
        };
    }

    shouldDisplayRow(row: TableRow, includeChildAccounts: boolean): boolean {
        // Hide all collections rows until the user selects a customer
        const selectedCustomerId = this.selectedRow?.data?.getString("customer_id", null);
        if (!this.selectedRow) {
            return false;
        }

        const rowCustomerId = row.data.getString("customer_id", null);
        const rowBridgeId = row.data.getString("bridge_id", null);

        if (includeChildAccounts) {
            // Show rows where the customer or bridge ID matches the selected customer
            return selectedCustomerId === rowCustomerId || selectedCustomerId === rowBridgeId;
        } else {
            // Only show rows where the customer ID matches the selected customer
            return selectedCustomerId === rowCustomerId;
        }
    }
    // END - Custom filter logic

    tableGroupByOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const customerId = modelRow.getString("customer_id", null);
        if (customerId) {
            const expandedRowData = this.groupByMap.get(customerId)?.expandedRowData || [];
            const rows: ModelRow[] = [];
            for (const rowData of expandedRowData) {
                const row = new ModelRow("lme/ar/collections", false);
                row.set("customer_id", rowData.customerId);
                row.set("customer_name", rowData.customerName);
                row.set("customer_currency", rowData.customerCurrency);
                row.set("invoice_currency_type", rowData.invoiceCurrencyType);
                row.set("customer_action_code", rowData.customerActionCode);
                row.set("customer_action_code_descr", rowData.customerActionCodeDescr);
                row.set("customer_average_pay_days", rowData.customerAveragePayDays);
                row.set("customer_bill_due_days", rowData.customerBillDueDays);
                row.set("default_next_action_code", rowData.defaultNextActionCode);
                row.set("default_next_action_days", rowData.defaultNextActionDays);
                row.set("default_next_action_descr", rowData.defaultNextActionDescr);
                row.set("age_days", rowData.ageDays);
                row.updateCurrency("balance", rowData.balance);
                row.updateCurrency("past_due_total", rowData.pastDueTotal);
                row.set("is_bridge_account", rowData.isBridgeAccount);
                row.set("bridge_id", rowData.bridgeId);
                rows.push(row);
            }

            if (rows.length > 0) {
                const expandPanel = event.expandComponentParent as Panel;
                const layoutGroupByExpand = expandPanel.findComponentById("layoutGroupByExpand") as AutogenLayoutCollectionsGroupByExpand;
                layoutGroupByExpand.tableGroupByExpand.displayComponentData(null, rows, 0);
                layoutGroupByExpand.tableGroupByExpand.addSelectionListener(async () => {
                    if (this.selectedRow) {
                        if (this.selectedRow.table !== layoutGroupByExpand.tableGroupByExpand) {
                            this.selectedRow.table.selectedRows = [];
                        }
                    }
                    this.selectedRow = layoutGroupByExpand.tableGroupByExpand.selectedRow ?? null;
                    this.layoutCollectionsTable.selectedCustomerRow = (this.selectedRow?.data ?? null) as ModelRow;
                    this.refreshCustomFilter(false);
                    await this.layoutCollectionsTable.updateTableFooter();
                });
            }
        }
    }

    adjustBalanceColumnHeader() {
        const balanceHeadingCell = this.tableGroupBy.getColumnByCaption("Balance")?.headingCell;
        if (balanceHeadingCell) {
            balanceHeadingCell.paddingRight = this.switchGroupBy.checked ? 4 : 22;
        }
    }
}
